import { useEffect, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import { useAuth } from "@/contexts/AuthContext"
import { useOrgSettings, useUpdateOrgSettings } from "@/lib/queries"
import { ApiAccessPage } from "@/pages/ApiAccessPage"
import { OrgUsersPanel } from "@/components/OrgUsersPanel"
import { GroupsPanel } from "@/components/GroupsPanel"
import { PharmacySitesPanel } from "@/components/PharmacySitesPanel"
import { SsoChangeConfirmDialog, ssoChanges, type SsoChange } from "@/components/SsoChangeConfirmDialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

export function OrgAdminPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { data: orgSettings } = useOrgSettings()
  const showPharmacySites = (orgSettings?.max_sub_organizations ?? 0) > 0

  useEffect(() => {
    if (user && user.role !== "manager") {
      navigate({ to: "/" })
    }
  }, [user, navigate])

  if (!user || user.role !== "manager") return null

  return (
    <div className="p-6 max-w-[70%] mx-auto space-y-6">
      <h1 className="text-2xl font-semibold">Admin</h1>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          {user.quizzes_enabled && <TabsTrigger value="groups">Groups</TabsTrigger>}
          {user.api_access_enabled && <TabsTrigger value="api">API Access</TabsTrigger>}
          <TabsTrigger value="sso">SSO</TabsTrigger>
          {showPharmacySites && <TabsTrigger value="pharmacy-sites">Pharmacy Sites</TabsTrigger>}
        </TabsList>
        <TabsContent value="users">
          <OrgUsersPanel currentUserId={user.id} />
        </TabsContent>
        {user.quizzes_enabled && (
          <TabsContent value="groups">
            <GroupsPanel />
          </TabsContent>
        )}
        {user.api_access_enabled && (
          <TabsContent value="api">
            <ApiAccessPage />
          </TabsContent>
        )}
        <TabsContent value="sso">
          <SsoPanel />
        </TabsContent>
        {showPharmacySites && (
          <TabsContent value="pharmacy-sites">
            <PharmacySitesPanel currentUserId={user.id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

function SsoPanel() {
  const { data: org, isLoading, isError } = useOrgSettings()
  const updateSettings = useUpdateOrgSettings()
  const [ssoDomain, setSsoDomain] = useState("")
  const [entraTenantId, setEntraTenantId] = useState("")
  const [seeded, setSeeded] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingChanges, setPendingChanges] = useState<SsoChange[]>([])

  useEffect(() => {
    if (org && !seeded) {
      setSsoDomain(org.sso_domain ?? "")
      setEntraTenantId(org.entra_tenant_id ?? "")
      setSeeded(true)
    }
  }, [org, seeded])

  if (isLoading) return <p className="text-muted-foreground py-6">Loading...</p>
  if (isError) return <p className="text-destructive py-6">Could not load SSO settings.</p>

  if (org?.sub_organization) {
    return (
      <div className="max-w-lg space-y-4 py-2">
        <div className="space-y-2">
          <Label>Google SSO domain</Label>
          <Input value={org.sso_domain ?? "Not configured"} readOnly disabled />
        </div>
        <div className="space-y-2">
          <Label>Microsoft Entra tenant ID</Label>
          <Input value={org.entra_tenant_id ?? "Not configured"} readOnly disabled />
        </div>
        <p className="text-sm text-muted-foreground">
          SSO settings for this pharmacy site are managed by your parent organization.
        </p>
      </div>
    )
  }

  const doSave = async () => {
    try {
      await updateSettings.mutateAsync({
        sso_domain: ssoDomain.trim() || null,
        entra_tenant_id: entraTenantId.trim() || null,
      })
      toast.success("SSO settings saved")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save SSO settings")
    }
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    const changes = ssoChanges(
      {
        sso_domain: org?.sso_domain ?? null,
        entra_tenant_id: org?.entra_tenant_id ?? null,
      },
      {
        sso_domain: ssoDomain.trim() || null,
        entra_tenant_id: entraTenantId.trim() || null,
      }
    )
    if (changes.length === 0) {
      void doSave()
      return
    }
    setPendingChanges(changes)
    setConfirmOpen(true)
  }

  return (
    <form onSubmit={handleSave} className="max-w-lg space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="sso-domain">Google SSO domain</Label>
        <Input
          id="sso-domain"
          value={ssoDomain}
          onChange={(e) => setSsoDomain(e.target.value)}
          placeholder="example.com"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="entra-tenant">Microsoft Entra tenant ID</Label>
        <Input
          id="entra-tenant"
          value={entraTenantId}
          onChange={(e) => setEntraTenantId(e.target.value)}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Users with verified emails on this domain can sign in with SSO. Leave blank to disable.
      </p>
      <Button type="submit" disabled={updateSettings.isPending}>
        {updateSettings.isPending ? "Saving..." : "Save SSO settings"}
      </Button>
      <SsoChangeConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        changes={pendingChanges}
        onConfirm={() => {
          setConfirmOpen(false)
          void doSave()
        }}
      />
    </form>
  )
}
